/**
 * Command Centre — route shell that renders the matched child tab.
 *
 * All navigation is handled by the main sidebar (Sidebar.tsx).
 * Each sub-path maps to a lazy-loaded tab component.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import TabErrorBoundary from '@/components/TabErrorBoundary';

// Per-tab error boundary helper. Wraps the lazy element so a render
// crash in one tab leaves the rest of CC operable (Fable C6, 2026-06-13).
function withBoundary(name: string, node: ReactNode) {
  return <TabErrorBoundary tabName={name}>{node}</TabErrorBoundary>;
}

// Lazy-load each tab to keep the initial chunk small
const Today = lazy(() => import('./tabs/Today'));
// The old Overview's totals and charts move to the business dashboard (Nino, 12 Oct); kept
// off the menu at /command-centre/overview until the dashboard shows them.
const ScoringOverview = lazy(() => import('./tabs/ScoringOverview'));
const ContentGaps = lazy(() => import('./tabs/ContentGaps'));
const Systems = lazy(() => import('./tabs/Systems'));
const RubricManagement = lazy(() => import('./tabs/RubricManagement'));
const ProgrammeManagement = lazy(() => import('./tabs/ProgrammeManagement'));
const Facilitators = lazy(() => import('./tabs/Facilitators'));
const ContentPipelineMonitor = lazy(() => import('./tabs/ContentPipelineMonitor'));
// Was the Content Catalogue (CC brief 5, Nino approved option A, 7 Oct).
const ReviewAndWording = lazy(() => import('./tabs/ReviewAndWording'));
const Learners = lazy(() => import('./tabs/Learners'));
// 2026-08-20 (Product [SPEC — CC request queue]) — Focus programme drill queue.
const RequestQueue = lazy(() => import('./tabs/RequestQueue'));
const OpsIncidents = lazy(() => import('./tabs/OpsIncidents'));
const WorkshopReview = lazy(() => import('./tabs/WorkshopReview'));
const NudgeCardLibrary = lazy(() => import('./tabs/NudgeCardLibrary'));
const KnowledgeCardLibrary = lazy(() => import('./tabs/KnowledgeCardLibrary'));
const ThemeManagement = lazy(() => import('./tabs/ThemeManagement'));
// 2026-10-06 (Nino approved Design's sketch): Image Library, first in Content.
const ImageLibrary = lazy(() => import('./tabs/ImageLibrary'));
// 2026-10-06 (Nino approved the observer seat; Design frame 12): every drill running now.
const Operations = lazy(() => import('./tabs/Operations'));
// Career Library — full-corpus browser + editor (Scoring memo 2026-07-16).
// Previously CareerRoles (INDOTASK-only, 51 rows). Now spans ~2,250
// rows across O*NET / ESCO / WEF / INDOTASK with Future Fit visible.
// File name kept per Scoring's note to avoid rewiring routes.
// (Nino 2026-06-20). Lets Ops curate the short forms shown in the
// learner picker without code changes — edits write to
// `data/indotask/display_names.json` and invalidate the runtime cache.
const CareerRoles = lazy(() => import('./tabs/CareerRoles'));
// Venues — 2026-06-21 (Nino, picker E). Org venue management with the
// OSM/Leaflet/Nominatim picker. Shares /api/org/venues with OP.
const Venues = lazy(() => import('./tabs/Venues'));
// Coaching activity Calendar — 2026-06-05. Operations-tab surface
// unifying workshops, AI Coach + Live Coach drills, and open slots
// from /api/admin/coaching-calendar.
const CoachingCalendar = lazy(() => import('./tabs/CoachingCalendar'));

function TabLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="flex flex-col items-center gap-2">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

export default function CommandCentre() {
  return (
    <Suspense fallback={<TabLoader />}>
      <Routes>
        <Route index element={withBoundary('Today', <Today />)} />
        <Route path="overview" element={withBoundary('Overview', <ScoringOverview />)} />
        {/* Analytics + Workshop analytics merged (Nino approved Design's menu, 12 Oct). */}
        <Route path="content-gaps" element={withBoundary('Content gaps', <ContentGaps />)} />
        <Route path="rubrics" element={withBoundary('Rubrics', <RubricManagement />)} />
        <Route path="systems" element={withBoundary('Systems', <Systems />)} />
        {/* 2026-10-06 (Nino's audit): Test Workshops and the never-reached
            Validation / Scoring config / Analytics exports / A/B test
            screens are removed with their files. */}
        <Route path="programmes" element={withBoundary('Programme progress', <ProgrammeManagement />)} />
        <Route path="facilitators" element={withBoundary('Facilitators', <Facilitators />)} />
        {/* The Leaderboard is folded into Facilitators (brief 7, Nino 7 Oct). */}
        <Route path="facilitator-trust" element={<Navigate to="/command-centre/facilitators?tab=everyone" replace />} />
        <Route path="pipeline" element={withBoundary('Content pipeline', <ContentPipelineMonitor />)} />
        <Route path="catalogue" element={withBoundary('Review & wording', <ReviewAndWording />)} />
        <Route path="learners" element={withBoundary('Learners', <Learners />)} />
        <Route path="request-queue" element={withBoundary('Requests', <RequestQueue />)} />
        {/* Ops & incidents — C1 phase 1b (Fable 2026-06-12). */}
        <Route path="ops" element={withBoundary('Incidents', <OpsIncidents />)} />
        {/* C — Port FP ReviewTab → CC (Fable 2026-06-13 task #131). */}
        <Route path="drill-reviews" element={withBoundary('Drill reviews', <WorkshopReview />)} />
        <Route path="workshop-review" element={<Navigate to="/command-centre/drill-reviews" replace />} />
        <Route path="nudge-cards" element={withBoundary('Nudge cards', <NudgeCardLibrary />)} />
        <Route path="knowledge" element={withBoundary('Knowledge cards', <KnowledgeCardLibrary />)} />
        <Route path="calendar" element={withBoundary('Calendar', <CoachingCalendar />)} />
        <Route path="operations" element={withBoundary('Operations', <Operations />)} />
        <Route path="live-drills" element={<Navigate to="/command-centre/operations" replace />} />
        <Route path="image-library" element={withBoundary('Images', <ImageLibrary />)} />
        <Route path="themes" element={withBoundary('Themes', <ThemeManagement />)} />
        <Route path="career-roles" element={withBoundary('Career library', <CareerRoles />)} />
        <Route path="venues" element={withBoundary('Venues', <Venues />)} />
        <Route path="*" element={<Navigate to="/command-centre" replace />} />
      </Routes>
    </Suspense>
  );
}
